import { yupResolver } from "@hookform/resolvers/yup";
import { type FC } from "react";
import { type DeepPartial } from "react-hook-form";

import { isAggregatedEntity } from "@ds/model/entity-model";

import {
  DetailsCard,
  DetailsCardEditActionsMenu,
  DetailsCardRow,
  EditableDetailsCardDecorator,
  EditableDetailsCardRowDecorator,
} from "@ds/components/details-view";
import { AggregatedValue } from "@ds/components/entities";
import { DescriptionControl } from "@ds/components/forms/control/description-control";
import { formatRelativeDate } from "@ds/utils/date";
import { getProperty } from "@ds/utils/properties";

import { formatMixingStrategy } from "../../utils/helpers";
import { updateValidationSchema } from "../../utils/validation-schema";
import { CampaignMixingStrategyControl } from "../forms/controls/campaign-mixing-strategy-control";
import { CampaignNameControl } from "../forms/controls/campaign-name-control";
import { CampaignDetailsInfoCardActionsMenu } from "./campaign-details-info-card-actions-menu";

export const CampaignDetailsInfoCard: FC<{
  campaign: Campaign;
  onSubmit: (changedFields: DeepPartial<Campaign>) => void;
  onDuplicate: () => void;
}> = ({ campaign, onSubmit, onDuplicate }) => (
  <EditableDetailsCardDecorator
    initialValue={campaign}
    validationResolver={yupResolver(updateValidationSchema.info)}
    onSubmit={onSubmit}
  >
    {cardProps => (
      <DetailsCard
        title="Info"
        icon="info-circle"
        actions={
          !cardProps.isDirty && !cardProps.isEditing ? (
            <CampaignDetailsInfoCardActionsMenu campaign={cardProps.entity} onDuplicate={onDuplicate} />
          ) : (
            <DetailsCardEditActionsMenu
              isEditing={cardProps.isEditing}
              isDirty={cardProps.isDirty}
              onReset={cardProps.onResetForm}
              onSubmit={cardProps.handleSubmit(cardProps.onSubmitForm)}
            />
          )
        }
      >
        <EditableDetailsCardRowDecorator
          field="name"
          input={<CampaignNameControl control={cardProps.control} autoFocus excludeLabel />}
          shouldHideEdit={isAggregatedEntity(cardProps.entity)}
          {...cardProps}
        >
          {rowProps => (
            <DetailsCardRow label="Name" editableSettings={rowProps} isRequired>
              <AggregatedValue entity={cardProps.entity} field="name" shouldShowEditedValue={rowProps.isDirty} />
            </DetailsCardRow>
          )}
        </EditableDetailsCardRowDecorator>

        <EditableDetailsCardRowDecorator
          field="description"
          input={<DescriptionControl control={cardProps.control} autoFocus excludeLabel />}
          {...cardProps}
        >
          {rowProps => (
            <DetailsCardRow label="Description" editableSettings={rowProps}>
              <AggregatedValue entity={cardProps.entity} field="description" shouldShowEditedValue={rowProps.isDirty} />
            </DetailsCardRow>
          )}
        </EditableDetailsCardRowDecorator>

        <EditableDetailsCardRowDecorator
          field="mixing_strategy"
          input={<CampaignMixingStrategyControl control={cardProps.control} autoFocus excludeLabel />}
          {...cardProps}
        >
          {rowProps => (
            <DetailsCardRow label="Mixing Strategy" editableSettings={rowProps} isRequired>
              <AggregatedValue
                entity={cardProps.entity}
                field="mixing_strategy"
                formatter={(entity, field) => formatMixingStrategy(getProperty(entity, field))}
                shouldShowEditedValue={rowProps.isDirty}
              />
            </DetailsCardRow>
          )}
        </EditableDetailsCardRowDecorator>

        <DetailsCardRow label="# Playlists">
          <AggregatedValue entity={cardProps.entity} field="campaign_items_count" />
        </DetailsCardRow>

        <DetailsCardRow label="Last Updated">
          <AggregatedValue
            entity={cardProps.entity}
            field="audit.updated_at"
            formatter={(entity, field) => formatRelativeDate(getProperty(entity, field))}
          />
        </DetailsCardRow>
      </DetailsCard>
    )}
  </EditableDetailsCardDecorator>
);
